import { useNavigate, useParams } from 'react-router';
import ActionHeader from '../../../components/actionHeader';
import Page from '../../../components/page';
import { Button, Form, message, Spin, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  createDraftTemplate,
  fetchLinkedMachines,
  fetchTemplateById,
  fetchTemplateCauses,
  fetchTemplateIssues,
  selectDraftTemplate,
  selectIsFetchingTemplates,
  selectTemplateCurrentCauses,
  selectTemplateCurrentIssues,
  setLinkedMachines,
  updateDraftTemplate,
} from '../../../slices/templates';
import { StepTitle } from './style';
import { useAppDispatch } from '../../../store';
import TemplateInformation from './information';
import { NewOrExisting, Template } from '../../../types';
import AddIssue from './addIssue';
import AddCause from './addCause';
import SolutionGrid from './solutionGrid';
import AddRoutine from './addRoutine';
import {
  createSolutionGrid,
  createTemplate,
  updateSolutionGrid,
  updateTemplate,
} from '../../../utils/httpRequest';
import { initSolutionGrid } from '../../../utils/dataMapper';
import {
  fetchSolutionGrid,
  selectCurrentSolutionGridVersions,
  selectSolutionGrids,
  setInitSolutionGridState,
} from '../../../slices/solutionGrids';
import { selectCurrentIssues } from '../../../slices/issues';

const steps = [
  {
    title: <StepTitle>Template Information</StepTitle>,
    description: 'Fill out information & link machine(s)',
  },
  {
    title: <StepTitle>Add Issue (Optional)</StepTitle>,
    description: 'Fill out issue information',
  },
  {
    title: <StepTitle>Add Cause (Optional)</StepTitle>,
    description: 'Fill out cause information',
  },
  {
    title: <StepTitle>Solution Grid (Optional)</StepTitle>,
    description: 'Connect issue(s) & cause(s)',
  },
  {
    title: <StepTitle>Add Routine (Optional)</StepTitle>,
    description: 'Fill out routine information',
  },
];

const TemplateForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFetchingTemplates = useSelector(selectIsFetchingTemplates);

  const draftTemplate = useSelector(selectDraftTemplate);
  const issues = useSelector(selectTemplateCurrentIssues);
  const causes = useSelector(selectTemplateCurrentCauses);

  const solutionGrids = useSelector(selectSolutionGrids);
  const currentSolutionGridVersions = useSelector(
    selectCurrentSolutionGridVersions
  );

  const stepComponents = [
    <TemplateInformation
      key={0}
      template={draftTemplate as Template}
      form={form}
      templateId={id}
    />,
    <AddIssue key={1} form={form} />,
    <AddCause key={2} form={form} />,
    <SolutionGrid key={3} />,
    <AddRoutine key={4} form={form} />,
  ];

  const validateInformation = async (shouldGoNext: boolean = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const newTemplate = {
        ...draftTemplate,
        ...form.getFieldsValue(),
        documents: draftTemplate?.documents || [],
      };

      const res = await (draftTemplate?.templateID
        ? updateTemplate(newTemplate)
        : createTemplate(newTemplate));

      dispatch(
        updateDraftTemplate({
          ...draftTemplate,
          ...res.data,
        })
      );
      form.resetFields();

      if (shouldGoNext) {
        setStep(step + 1);
      }
    } catch (error: any) {
      console.log(error);
      messageApi.error(error || 'Submit Failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    validateInformation();
    navigate(-1);
  };

  const initNewSolutionGrid = async () => {
    try {
      setLoading(true);
      const { newSolutionGrid, updatedSolutionGrid } = initSolutionGrid({
        issues: issues || [],
        causes: causes || [],
        templateID: String(draftTemplate?.templateID),
        existingSolutionGrids: solutionGrids || [],
        versionID: currentSolutionGridVersions?.versionID,
      });

      await Promise.all([
        newSolutionGrid?.length &&
          createSolutionGrid(
            {
              newOrExisting: solutionGrids.length
                ? NewOrExisting.EXISTING
                : NewOrExisting.NEW,
              versionName:
                currentSolutionGridVersions?.versionName || 'Default',
              versionID: currentSolutionGridVersions?.versionID,
              solutionGridDtoList: newSolutionGrid,
            },
            String(draftTemplate?.templateID)
          ),
        // updatedSolutionGrid?.length && updateSolutionGrid(updatedSolutionGrid),
      ]);

      setStep(3);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const submitNewSolutionGrid = async () => {
    try {
      setLoading(true);
      await updateSolutionGrid(
        solutionGrids,
        String(draftTemplate?.templateID)
      );
      setStep(step + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStepChange = (stepValue: number) => {
    if (id || stepValue < step) {
      form.resetFields();
      switch (stepValue) {
        case 0:
          setStep(stepValue);
          form.setFieldsValue(draftTemplate);
          break;
        case 3:
          initNewSolutionGrid();
          break;

        default:
          setStep(stepValue);
          break;
      }
    }
  };

  const handleNext = async () => {
    switch (step) {
      case 0:
        validateInformation();
        break;
      case 1:
        // if (issues?.length === 0) {
        //   messageApi.open({
        //     type: 'error',
        //     content: 'Please add at least 1 issue',
        //   });
        // } else {
        setStep(step + 1);
        form.resetFields();
        // }
        break;
      case 2:
        // if (causes?.length === 0) {
        //   messageApi.open({
        //     type: 'error',
        //     content: 'Please add at least 1 cause',
        //   });
        // } else {
        initNewSolutionGrid();
        // }
        break;
      case 3:
        submitNewSolutionGrid();
        break;
      default:
        navigate(-1);
        break;
    }
  };

  useEffect(() => {
    if (id === String(draftTemplate?.templateID) && step === 0) {
      form.setFieldsValue(draftTemplate);
    }
  }, [id, draftTemplate?.templateID]);

  useEffect(() => {
    if (id) {
      dispatch(fetchTemplateById(id));
      dispatch(fetchSolutionGrid({ templateID: id }));
      dispatch(fetchTemplateIssues({ templateId: id }));
      dispatch(fetchTemplateCauses({ templateId: id }));
      dispatch(fetchLinkedMachines(id));
    } else {
      dispatch(createDraftTemplate());
      dispatch(
        setInitSolutionGridState({
          solutionGridDtoList: [],
        })
      );
      dispatch(setLinkedMachines([]));
    }
  }, []);

  return (
    <Page
      header={
        <ActionHeader
          title={id ? draftTemplate?.name : t('template_add_new')}
          showBack
          onBack={() => navigate(-1)}
          endSlot={
            <>
              <Button type="primary" ghost onClick={handleSaveDraft}>
                {t('save_as_draft')}
              </Button>
              <Button type="primary" onClick={handleNext}>
                {t('next')}
              </Button>
            </>
          }
        >
          <Steps
            items={steps.map((s, index) => ({
              ...s,
              disabled: !id && index >= step,
            }))}
            current={step}
            onChange={handleStepChange}
          />
        </ActionHeader>
      }
    >
      {contextHolder}
      <Spin fullscreen spinning={loading || isFetchingTemplates} />
      {draftTemplate && (
        <Form form={form} layout="vertical">
          {stepComponents[step]}
        </Form>
      )}
    </Page>
  );
};

export default TemplateForm;
